/**
 * CreateActivityModal
 *
 * Reactstrap-based modal wrapper around `ActivityFormFlow`. Used by the dashboard
 * and onboarding checklist so users can create activities/assets without
 * navigating away from the page they started from.
 *
 * The `initialMethod` prop drives which of the four entry paths opens:
 * - `log-activity`      → jumps to Activity Type step
 * - `bulk-upload`       → jumps to Activity Type step, then Upload / Preview / Complete
 * - `from-inventory`    → jumps to Asset Select step (asset wizard; bulk-XLSX
 *                          path is folded into asset-select itself)
 */

import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ActivityFormFlow } from './ActivityFormFlow';
import { DataMethod } from './types';

const MODAL_TITLES: Record<DataMethod, string> = {
  [DataMethod.ByActivity]: 'Add Activity',
  [DataMethod.BulkUpload]: 'Bulk Upload Activities',
  [DataMethod.ByAsset]: 'Add Asset',
  [DataMethod.ConnectUtility]: 'Connect Utility',
  [DataMethod.ConnectFinancial]: 'Connect Financial',
  [DataMethod.ConnectTravel]: 'Connect Travel',
  [DataMethod.ScanInvoice]: 'Scan Invoice',
  [DataMethod.BySpend]: 'By Spend',
  [DataMethod.BulkSpend]: 'Bulk Spend Import',
  [DataMethod.EmployeeSurvey]: 'By Employee Survey',
};

interface CreateActivityModalProps {
  isOpen: boolean;
  /** Toggle/close handler. Also used as the form's `onExit` so submit-success closes the modal. */
  toggle: () => void;
  initiativeId: string;
  initialMethod: DataMethod;
}

export const CreateActivityModal = ({
  isOpen,
  toggle,
  initiativeId,
  initialMethod,
}: CreateActivityModalProps) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} size='xl' scrollable>
      <ModalHeader toggle={toggle}>{MODAL_TITLES[initialMethod]}</ModalHeader>
      <ModalBody>
        {/* Remount the form on each open so internal state resets cleanly between sessions. */}
        {isOpen ? (
          <ActivityFormFlow
            key={initialMethod}
            initiativeId={initiativeId}
            initialMethod={initialMethod}
            onExit={toggle}
          />
        ) : null}
      </ModalBody>
    </Modal>
  );
};
